import { Loader } from 'google-maps'
import MarkerClusterer from '@google/markerclustererplus'
import memoize from 'lodash/memoize'
import { LV03toWGS, WGStoLV03 } from 'swiss-projection'

const loadGoogleApis = memoize(() => {
  return new Loader(window.googleApisKey).load()
})

const markerClustererStyles = [32, 44, 56, 68].map(n => ({
  width: n,
  height: n,
}))

const getClusterClass = useCircles => {
  let clusterClass =
    'marker-cluster fw-500 t-label-regular flex-container align-middle'
  if (useCircles) {
    clusterClass += ' circle'
  }
  return clusterClass
}

const isSwissCoordinate = (number) => Math.abs(number) > 180

const convertFromSwiss = latLng => {
  const isSwiss = isSwissCoordinate(latLng.lat) || isSwissCoordinate(latLng.lng)
  if (isSwiss) {
    const [[[lng, lat]]] = LV03toWGS([[[latLng.lat, latLng.lng]]])
    return { lat, lng }
  }
  return latLng
}

const convertToSwiss = latLng => {
  const [[[lat, lng]]] = WGStoLV03([[[latLng.lng, latLng.lat]]])
  return { lat, lng }
}

class GoogleMap {
  constructor(element, options = {}) {
    this.element = element
    this.options = options
    this.activeMarker = null
    this.markerData = []
    this.markers = []
    this.circles = []
    this.disableEmittingNextChangeEvent = false

    this.createMap()

    this.useCircles = options.useCircles
    this.updateMarkerIcons()
  }

  async createMap() {
    const google = await loadGoogleApis()

    this.map = new google.maps.Map(this.element, {
      zoom: Number(this.options.zoom) || 10,
      disableDefaultUI: true,
      ...(this.options.mapOptions || {}),
    })

    this.oms = new window.OverlappingMarkerSpiderfier(this.map, {
      markersWontMove: true,
      markersWontHide: true,
      basicFormatEvents: true,
      // keepSpiderfied: true,
      circleFootSeparation: 48,
      spiralFootSeparation: 48,
    })

    if (!this.options.noClusterer) {
      this.markerClusterer = new MarkerClusterer(this.map, [], {
        styles: markerClustererStyles,
        clusterClass: getClusterClass(),
        maxZoom: 15,
      })
    }

    this.infoWindow = new google.maps.InfoWindow({ minWidth: 272 })
    if (this.options.infowindowContent) {
      this.infoWindow.setContent(this.options.infowindowContent)
    }
    this.infoWindow.addListener('closeclick', () => {
      this.activeMarker = null
      this.updateMarkerIcons()
    })

    if (this.options.initialBounds) {
      this.setMapBounds(this.options.initialBounds)
    }

    const handleChange = () => {
      if (!this.disableEmittingNextChangeEvent) {
        const bounds = this.prepareBounds(this.map.getBounds())
        const { onBoundsChange } = this.options
        if (typeof onBoundsChange === 'function') {
          onBoundsChange(bounds)
        }
      }
    }

    this.map.addListener('dragend', () => {
      handleChange()
    })
    this.map.addListener('zoom_changed', () => {
      this.hideMarkersWhereAddressHidden()
      handleChange()
    })
  }

  async fitToMarkers() {
    const google = await loadGoogleApis()

    if (this.markers.length === 0) return

    const bounds = new google.maps.LatLngBounds()
    this.markerData.forEach(data => {
      bounds.extend(this.prepareCoordinates(data.coordinates))
    })
    this.circles.forEach(circle => {
      bounds.union(circle.getBounds())
    })

    this.fitBoundsWhitoutEmittingEvents(bounds)
  }

  async setMarkers(markerData) {
    const google = await loadGoogleApis()

    if (this.markers != null) {
      if (!this.options.noClusterer) {
        this.markerClusterer.removeMarkers(this.markers)
      }
      this.markers.forEach(marker => marker.setMap(null))
      this.circles.forEach(circle => circle.setMap(null))
      this.circles = []
    }

    this.markerData = markerData.filter(m => !(m.coordinates.lat === 0 && m.coordinates.lng === 0))
    console.log(this.markerData)
    this.markers = markerData.map(data => {
      const coordinates = this.prepareCoordinates(data.coordinates)

      const marker = new google.maps.Marker({
        // map: this.map,
        position: coordinates,
        zIndex: markerData.zIndex,
      })
      this.oms.addMarker(marker)

      const handleMarkerInteraction = () => {
        const { onMarkerClick } = this.options
        if (typeof onMarkerClick === 'function') {
          onMarkerClick(data)
        }
        this.infoWindow.open(this.map, marker)
        this.activeMarker = data
        this.updateMarkerIcons()
      }

      google.maps.event.addListener(
        marker,
        'spider_click',
        handleMarkerInteraction
      )

      if (data.displayAddress === false) {
        const circle = new google.maps.Circle({
          map: this.map,
          center: coordinates,
          radius: 400,
          strokeColor: '#113c63',
          strokeOpacity: 1,
          strokeWeight: 1,
          fillColor: '#113c63',
          fillOpacity: 0.2,
        })
        this.circles.push(circle)
        google.maps.event.addListener(circle, 'click', handleMarkerInteraction)
      }

      return marker
    })

    if (this.markers.length && !this.options.noClusterer) {
      this.markerClusterer.addMarkers(this.markers)
    }

    this.hideMarkersWhereAddressHidden()
    this.updateMarkerIcons()
  }

  setUseCircles(useCircles) {
    this.useCircles = useCircles
    if (!this.options.noClusterer) {
      this.markerClusterer.setClusterClass(getClusterClass(useCircles))
    }
    this.updateMarkerIcons()
  }

  getMarkerIconPath(marker, useHoveredState) {
    let iconPath = this.options.markersDir

    if (marker.iconOnMap) {
      iconPath += this.useCircles ? 'circle/' : 'rectangle/'
      if (useHoveredState) {
        iconPath += 'hover/'
      }
      iconPath += `${marker.iconOnMap}.svg`
    } else if (marker.icon === 'pink') {
      iconPath += 'map-pin--red.svg'
    } else if (marker.icon === 'grey') {
      iconPath += 'map-pin--gray.svg'
    } else {
      iconPath += 'map-pin.svg'
    }
    return iconPath
  }

  async updateMarkerIcons() {
    const google = await loadGoogleApis()

    const size = new google.maps.Size(32, 32)
    this.markerData.forEach((data, index) => {
      const url = this.getMarkerIconPath(data, data === this.activeMarker)
      const anchorY = typeof data.icon === 'string' ? 32 : 16
      const anchor = new google.maps.Point(16, anchorY)

      const image = {
        url,
        size,
        anchor,
      }

      this.markers[index].setIcon(image)
    })
  }

  prepareCoordinates(latLng) {
    if (this.options.swissCoordinates) {
      return convertFromSwiss(latLng)
    }
    return latLng
  }

  prepareBounds(bounds) {
    let ne = bounds.getNorthEast().toJSON()
    let sw = bounds.getSouthWest().toJSON()
    if (this.options.swissCoordinates) {
      ne = convertToSwiss(ne)
      sw = convertToSwiss(sw)
    }
    return { north: ne.lat, east: ne.lng, west: sw.lng, south: sw.lat }
  }

  async setMapBounds({ north, west, south, east }) {
    const google = await loadGoogleApis()
    let ne = { lat: north, lng: east }
    let sw = { lat: south, lng: west }
    if (this.options.swissCoordinates) {
      ne = convertFromSwiss(ne)
      sw = convertFromSwiss(sw)
    }
    const bounds = new google.maps.LatLngBounds(sw, ne)
    this.fitBoundsWhitoutEmittingEvents(bounds)
  }

  hideMarkersWhereAddressHidden() {
    const zoom = this.map.getZoom()
    const showCircles = zoom >= 12
    this.markerData.forEach((data, index) => {
      if (data.displayAddress === false) {
        this.markers[index].setVisible(!showCircles)
      }
    })
    this.circles.forEach(circle => circle.setVisible(showCircles))
  }

  async fitBoundsWhitoutEmittingEvents(bounds) {
    const google = await loadGoogleApis()
    this.disableEmittingNextChangeEvent = true
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.disableEmittingNextChangeEvent = false
    })
    this.map.fitBounds(bounds, 0)
  }
}

export default GoogleMap
